import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { farmsApi, parcelsApi, alertsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { MapPin, Loader2, Info } from 'lucide-react';

// Fix leaflet default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const cropColors = {
  blé: '#f59e0b',
  maïs: '#eab308',
  vigne: '#7c3aed',
  tournesol: '#f97316',
  colza: '#84cc16',
  default: '#10b981',
};

const MapPage = () => {
  const [parcels, setParcels] = useState([]);
  const [farms, setFarms] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [parcelsRes, farmsRes, alertsRes] = await Promise.all([
          parcelsApi.getAll(),
          farmsApi.getAll(),
          alertsApi.getAll({ status: 'active' }),
        ]);
        setParcels(parcelsRes.data);
        setFarms(farmsRes.data);
        setAlerts(alertsRes.data);
      } catch {
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const parcelsWithCoords = parcels.filter((p) => p.latitude != null && p.longitude != null);
  const mapCenter = parcelsWithCoords.length > 0
    ? [parcelsWithCoords[0].latitude, parcelsWithCoords[0].longitude]
    : [46.603354, 1.888334]; // France center

  const getFarmName = (farmId) => farms.find((f) => f.id === farmId)?.name || '—';

  const handleParcelClick = async (parcel) => {
    setSelectedParcel(parcel);
    setWeather(null);
    if (parcel.latitude && parcel.longitude) {
      setWeatherLoading(true);
      try {
        const res = await parcelsApi.getWeather(parcel.id);
        setWeather(res.data);
      } catch {
        // No weather if no coords or API error
      } finally {
        setWeatherLoading(false);
      }
    }
  };

  const createParcelIcon = (parcel) => {
    const crop = parcel.crop_type?.toLowerCase() || 'default';
    const color = Object.entries(cropColors).find(([k]) => crop.includes(k))?.[1] || cropColors.default;
    return L.divIcon({
      html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      className: '',
    });
  };

  const weatherCodeLabel = (code) => {
    if (code === 0) return 'Ciel dégagé';
    if (code <= 3) return 'Nuageux';
    if (code <= 67) return 'Pluie';
    if (code <= 77) return 'Neige';
    if (code <= 82) return 'Averses';
    return 'Orageux';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Carte</h1>
        <p className="text-muted-foreground mt-1">
          {parcelsWithCoords.length} parcelle{parcelsWithCoords.length !== 1 ? 's' : ''} géolocalisée{parcelsWithCoords.length !== 1 ? 's' : ''} sur {parcels.length} au total
        </p>
      </div>

      {parcels.length > 0 && parcelsWithCoords.length === 0 && (
        <div className="flex items-center gap-3 p-4 rounded-lg border bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400">
          <Info className="h-5 w-5 shrink-0" />
          <p className="text-sm">Aucune parcelle n'a de coordonnées GPS. Modifiez vos parcelles pour ajouter une latitude et longitude.</p>
        </div>
      )}

      <div className="flex gap-4 h-[calc(100vh-16rem)] min-h-[500px]">
        {/* Map */}
        <div className="flex-1 rounded-xl overflow-hidden border">
          <MapContainer
            center={mapCenter}
            zoom={parcelsWithCoords.length > 0 ? 13 : 6}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {parcels.filter(p => p.boundary?.length >= 3).map((parcel) => {
              const crop = parcel.crop_type?.toLowerCase() || 'default';
              const color = Object.entries(cropColors).find(([k]) => crop.includes(k))?.[1] || cropColors.default;
              return (
                <Polygon
                  key={`poly-${parcel.id}`}
                  positions={parcel.boundary}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.2, weight: 2 }}
                  eventHandlers={{ click: () => handleParcelClick(parcel) }}
                >
                  <Tooltip sticky>{parcel.name}</Tooltip>
                </Polygon>
              );
            })}
            {parcelsWithCoords.map((parcel) => (
              <Marker
                key={parcel.id}
                position={[parcel.latitude, parcel.longitude]}
                icon={createParcelIcon(parcel)}
                eventHandlers={{ click: () => handleParcelClick(parcel) }}
              >
                <Popup>
                  <div className="min-w-[150px]">
                    <p className="font-semibold">{parcel.name}</p>
                    <p className="text-xs text-gray-500">{getFarmName(parcel.farm_id)}</p>
                    {parcel.crop_type && <p className="text-xs mt-1">Culture: {parcel.crop_type}</p>}
                    {parcel.area && <p className="text-xs">Surface: {parcel.area} ha</p>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Side panel */}
        <div className="w-72 flex flex-col gap-3 overflow-y-auto">
          {selectedParcel ? (
            <>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    {selectedParcel.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <p className="text-muted-foreground">{getFarmName(selectedParcel.farm_id)}</p>
                  {selectedParcel.crop_type && <p>Culture : <span className="font-medium">{selectedParcel.crop_type}</span></p>}
                  {selectedParcel.soil_type && <p>Sol : <span className="font-medium">{selectedParcel.soil_type}</span></p>}
                  {selectedParcel.area && <p>Surface : <span className="font-medium">{selectedParcel.area} ha</span></p>}
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedParcel.latitude?.toFixed(4)}, {selectedParcel.longitude?.toFixed(4)}
                  </p>
                </CardContent>
              </Card>

              {/* Weather */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Météo actuelle</CardTitle>
                </CardHeader>
                <CardContent>
                  {weatherLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Chargement...
                    </div>
                  ) : weather?.current ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Température</span>
                        <span className="font-medium">{weather.current.temperature_2m}°C</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Humidité</span>
                        <span className="font-medium">{weather.current.relative_humidity_2m}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Vent</span>
                        <span className="font-medium">{weather.current.wind_speed_10m} km/h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Précip.</span>
                        <span className="font-medium">{weather.current.precipitation} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Conditions</span>
                        <span className="font-medium">{weatherCodeLabel(weather.current.weather_code)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {selectedParcel.latitude ? 'Service météo indisponible' : 'Aucune coordonnée GPS'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* 7-day forecast */}
              {weather?.daily && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Prévisions 7 jours</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {weather.daily.time?.slice(0, 7).map((day, i) => (
                        <div key={day} className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground w-16">
                            {new Date(day).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                          </span>
                          <span>{weatherCodeLabel(weather.daily.weather_code[i])}</span>
                          <span className="font-medium">
                            {weather.daily.temperature_2m_min[i]}° / {weather.daily.temperature_2m_max[i]}°
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center text-sm text-muted-foreground">
                <MapPin className="h-8 w-8 mx-auto mb-2 opacity-30" />
                Cliquez sur un marqueur pour voir les détails de la parcelle
              </CardContent>
            </Card>
          )}

          {/* Parcels list */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Toutes les parcelles</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {parcels.map((p) => (
                <button
                  key={p.id}
                  onClick={() => p.latitude && handleParcelClick(p)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedParcel?.id === p.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  } ${!p.latitude ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{p.name}</span>
                    {!p.latitude && <span className="text-xs opacity-60 ml-1">sans GPS</span>}
                  </div>
                  {p.crop_type && <span className="text-xs opacity-70">{p.crop_type}</span>}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
